import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  FaMapMarkerAlt,
  FaStar,
  FaClock,
  FaCheck  
} from 'react-icons/fa'
import {
  DEFAULT_PACKAGES_ROUTE_ID,
  allPackageRouteTabs,
  getPackageListRouteGroupById
} from '../utils/packageEnquiry'
import { cmsApi } from '../services/cms'

type PackageCard = {
  id: string
  title: string
  location: string
  duration: string
  price: string
  rating: number
  reviews: number
  image: string
  tag?: string
  keyHighlights?: string[]
  inclusions?: string[]
  mainPackageId?: string
  collectionRouteId?: string
}

type CmsMainPackage = {
  id: string
  title?: string | null
  destination?: string | null
  description?: string | null
  websiteSlug?: string | null
}

type PackageRouteTab = {
  id: string
  label: string
  routeId: string
}

type CmsFeature = {
  title?: string | null
  description?: string | null
}

type CmsSubPackage = {
  id?: string | null
  packageId?: string | null
  name?: string | null
  title?: string | null
  destination?: string | null
  image?: string | null
  bannerImageUrl?: string | null
  galleryImageUrls?: string[] | null
  rating?: number | null
  location?: string | null
  durationDays?: number | null
  durationNights?: number | null
  duration?: string | null
  transport?: string | null
  startingPrice?: number | null
  startingPriceCurrency?: string | null
  highlights?: string[] | null
  features?: CmsFeature[] | null
  inclusions?: string[] | null
  isSoldOut?: boolean | null
}

const routeImages: Record<string, string[]> = {
  'maldives-ultra-luxury': [
    '/destination/Maldives%20Ultra%20Luxury/Cheval%20Blanc%20Randhali%20Maldives.jpg',
    '/destination/Maldives%20Ultra%20Luxury/Joali%20Maldives.webp',
    '/destination/Maldives%20Ultra%20Luxury/Ritz%20Carlton.jpg',
    '/destination/Maldives%20Ultra%20Luxury/Waldorf%20Astoria.webp'
  ],
  'maldives-luxury': [
    '/destination/Maldives Luxury/One & Only Reethi Rah.webp',
    '/destination/Maldives%20Luxury/Niyama%20Private%20Island.jpg',
    '/destination/Maldives%20Luxury/Four%20Seasons%20Kuda%20Hura.webp',
    '/destination/Maldives%20Luxury/Kuda%20Villingilli%20Resort%20Maldives.jpg',
    '/destination/Maldives%20Luxury/Sirru%20Fen%20Fushi%20%E2%80%93%20Private%20Lagoon%20Resort.webp'
  ],
  'maldives-premium': [
    '/destination/Maldives%20Premium/Centara%20Grand%20Lagoon%20Maldives.webp',
    '/destination/Maldives%20Premium/Hilton%20Maldives%20Amingiri.jpg',
    '/destination/Maldives%20Premium/Six%20Senses%20Laamu.jpg',
    '/destination/Maldives%20Premium/Constance%20Halaveli.webp',
    '/destination/Maldives%20Premium/Anantara%20Veli%20Maldives%20Resort.png',
    '/destination/Maldives%20Premium/Alila%20Kothaifaru.webp'
  ],
  'maldives-budget': [
    '/destination/Maldives Budget/Sheraton Maldives Maldives Full Moon Resort & Spa.webp',
    '/destination/Maldives%20Budget/Medhufushi%20Island%20Resort.jpeg',
    '/destination/Maldives%20Budget/Hard%20Rock%20Maldives.jpg',
    '/destination/Maldives%20Budget/Cinnamon%20Dhonveli.jpg',
    '/destination/Maldives%20Budget/NH%20Maldives%20Kuda%20Rah%20Resort.jpg',
    '/destination/Maldives%20Budget/Coco%20Bodu%20Hithi.avif',
    '/destination/Maldives%20Budget/Kurumba%20Maldives.webp',
    '/destination/Maldives%20Budget/Saii%20Lagoon.avif',
    '/destination/Maldives%20Budget/Velassaru%20Maldives.jpg'
  ],
  'georgia-georgian-escape': [
    'https://images.unsplash.com/photo-1513326738677-b964603b136d?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1533130061792-64b345e4a833?auto=format&fit=crop&w=1400&q=80',
    '/georgio.webp',
  ],
  'georgia-enchanting-georgia': [
    'https://images.unsplash.com/photo-1565004602747-718e1e4f7de3?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1605540436563-5bca919ae766?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1576675466969-38eeae4b41f6?auto=format&fit=crop&w=1400&q=80'
  ],
  'georgia-best-of-georgia': [
    'https://images.unsplash.com/photo-1558008258-3256797b43f3?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1555992828-ca4dbe41d294?auto=format&fit=crop&w=1400&q=80',
    '/m_Georgia.webp'
  ],
  'azerbaijan-azerbaijan-getaway': [
    '/m_Azerbaijan_destination_landscape_image_1_l_1447_1929.webp',
    '/m_Baku_destination_image_1_l_651_1000%20(1).webp',
    '/azerbaijani-mosque-flame-towers.webp'
  ],
  'kenya-safari-escape': [
    'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1503917988258-f87a78e3c995?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1400&q=80'
  ],
  'kenya-wildlife-discovery': [
    'https://images.unsplash.com/photo-1549366021-9f761d450615?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1503917988258-f87a78e3c995?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1400&q=80'
  ],
  'sri-lanka-signature-journeys': [
    'https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1544737151-6e4b8ca3f4f7?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1566296314736-6eaac1ca0cb9?auto=format&fit=crop&w=1400&q=80'
  ],
  'tanzania-safari-collection': [
    'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1400',
    'https://images.unsplash.com/photo-1523805009345-7448845a9e53?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1400&q=80'
  ]
}

const pickImageByRoute = (routeId: string, index: number) => {
  const images = routeImages[routeId]
  if (!images?.length) {
    return 'https://storage.googleapis.com/uxpilot-auth.appspot.com/84deee4332-9810b778ee3c9a1e3811.png'
  }
  return images[index % images.length]
}

const siteRegion = (import.meta.env.VITE_SITE_REGION || 'IN').toUpperCase()
const siteCountry = siteRegion === 'AE' ? 'United Arab Emirates' : 'India'
const defaultCurrency = siteRegion === 'AE' ? 'AED' : 'INR'
const PACKAGE_CONTEXT_STORAGE_KEY = 'g2v_package_context_v1'
const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const toSlug = (value?: string | null) =>
  (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const getCmsMainPackageRouteId = (mainPackage: CmsMainPackage) =>
  (mainPackage.websiteSlug || '').trim() ||
  toSlug(`${mainPackage.destination || ''}-${mainPackage.title || ''}`) ||
  toSlug(mainPackage.title) ||
  mainPackage.id

const buildCmsTabs = (mainPackages: CmsMainPackage[]): PackageRouteTab[] => {
  const deduped = new Map<string, PackageRouteTab>()
  mainPackages.forEach(mainPackage => {
    const routeId = getCmsMainPackageRouteId(mainPackage)
    if (!routeId || deduped.has(routeId)) return
    deduped.set(routeId, {
      id: mainPackage.id || routeId,
      label: mainPackage.title?.trim() || mainPackage.destination?.trim() || 'Packages',
      routeId
    })
  })
  return Array.from(deduped.values())
}

const normalizeToken = (value?: string | null) =>
  (value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

const formatPrice = (amount?: number | null, currency?: string | null) => {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return 'Contact Us'
  const normalizedCurrency = (currency || defaultCurrency).toUpperCase()
  return `${normalizedCurrency} ${amount.toLocaleString('en-IN')}`
}

const toDuration = (
  duration?: string | null,
  durationDays?: number | null,
  durationNights?: number | null
) => {
  const formatDaysNights = (days: number, nights: number) =>
    `${days} ${days === 1 ? 'Day' : 'Days'}/ ${nights} ${nights === 1 ? 'Night' : 'Nights'}`

  const parseCompactDuration = (value: string) => {
    const normalized = value.trim()
    const compactMatch = normalized.match(
      /(\d+)\s*(?:d|day|days)\s*\/\s*(\d+)\s*(?:n|night|nights)\b/i
    )
    if (!compactMatch) return null
    return formatDaysNights(Number(compactMatch[1]), Number(compactMatch[2]))
  }

  if (duration?.trim()) {
    const parsedDuration = parseCompactDuration(duration)
    if (parsedDuration) return parsedDuration
    return duration.trim()
  }
  if (
    typeof durationDays === 'number' && Number.isFinite(durationDays) &&
    typeof durationNights === 'number' && Number.isFinite(durationNights)
  ) {
    return formatDaysNights(durationDays, durationNights)
  }
  return 'Custom Duration'
}

const resolveMainPackageForRoute = (
  mainPackages: CmsMainPackage[],
  routeId: string
): CmsMainPackage | undefined => {
  if (!mainPackages.length) return undefined
  if (!routeId.trim()) return mainPackages[0]

  const directMatch = mainPackages.find(
    pkg =>
      pkg.id === routeId ||
      (pkg.websiteSlug || '').trim() === routeId ||
      getCmsMainPackageRouteId(pkg) === routeId ||
      toSlug(pkg.title) === routeId ||
      toSlug(`${pkg.destination || ''}-${pkg.title || ''}`) === routeId
  )
  if (directMatch) return directMatch

  const routeGroup = getPackageListRouteGroupById(routeId)
  if (!routeGroup) return undefined

  const destinationToken = normalizeToken(routeGroup.destinationName)
  const titleToken = normalizeToken(routeGroup.title)

  return mainPackages.find(pkg => {
    const packageDestination = normalizeToken(pkg.destination)
    const packageTitle = normalizeToken(pkg.title)
    const destinationMatches =
      !destinationToken ||
      packageDestination.includes(destinationToken) ||
      destinationToken.includes(packageDestination)
    const titleMatches =
      !titleToken ||
      packageTitle.includes(titleToken) ||
      titleToken.includes(packageTitle)
    return destinationMatches && titleMatches
  })
}

const rememberPackageContext = (
  mainPackageId: string,
  routeId: string,
  packageIds: string[]
) => {
  if (typeof window === 'undefined' || !mainPackageId || !packageIds.length) return
  try {
    const existingRaw = window.sessionStorage.getItem(PACKAGE_CONTEXT_STORAGE_KEY)
    const existing = existingRaw
      ? (JSON.parse(existingRaw) as Record<string, { mainPackageId: string; routeId: string }>)
      : {}
    packageIds.forEach(packageId => {
      existing[packageId] = { mainPackageId, routeId }
    })
    window.sessionStorage.setItem(PACKAGE_CONTEXT_STORAGE_KEY, JSON.stringify(existing))
  } catch {
    // Ignore storage errors.
  }
}

const mapSubPackages = (
  subPackages: CmsSubPackage[],
  selectedRouteId: string,
  mainPackageId: string,
  locationFallback?: string | null
): PackageCard[] =>
  subPackages
    .map((pkg, index): PackageCard | null => {
      const packageId = (pkg.packageId || pkg.id || '').trim()
      if (!packageId) return null
      const rating =
        typeof pkg.rating === 'number' && Number.isFinite(pkg.rating)
          ? Number(pkg.rating.toFixed(1))
          : 4.8
      const fallbackHighlights = (pkg.features || [])
        .map(f => {
          const t = f.title?.trim()
          const d = f.description?.trim()
          return t && d ? `${t}: ${d}` : d || t || ''
        })
        .filter(Boolean)
      const transferHighlight = `Transfer: ${pkg.transport?.trim() || 'To be confirmed'}`
      const sourceHighlights =
        Array.isArray(pkg.highlights) && pkg.highlights.length
          ? pkg.highlights
          : fallbackHighlights
      const remainingHighlights = sourceHighlights.filter(
        item => !/^transfer\s*:/i.test((item || '').trim())
      )
      return {
        id: packageId,
        title: pkg.title?.trim() || pkg.name?.trim() || 'Package',
        location: pkg.location?.trim() || pkg.destination?.trim() || locationFallback?.trim() || 'Destination',
        duration: toDuration(pkg.duration, pkg.durationDays, pkg.durationNights),
        price: formatPrice(pkg.startingPrice, pkg.startingPriceCurrency),
        rating,
        reviews: Math.max(12, Math.round(rating * 18)),
        image: pkg.bannerImageUrl || pkg.image || pkg.galleryImageUrls?.[0] || pickImageByRoute(selectedRouteId, index),
        tag: pkg.isSoldOut ? 'Sold Out' : undefined,
        keyHighlights: [transferHighlight, ...remainingHighlights],
        inclusions: Array.isArray(pkg.inclusions) ? pkg.inclusions.filter(Boolean).slice(0, 3) : [],
        mainPackageId,
        collectionRouteId: selectedRouteId
      }
    })
    .filter((card): card is PackageCard => card !== null)

const PackagesPage: React.FC = () => {
  const { id } = useParams()
  const [loadedRouteId, setLoadedRouteId] = useState('')
  const [isCmsLoading, setIsCmsLoading] = useState(false)
  const [cmsCards, setCmsCards] = useState<PackageCard[] | null>(null)
  const [cmsTabs, setCmsTabs] = useState<PackageRouteTab[]>([])
  const [cmsMainPackage, setCmsMainPackage] = useState<CmsMainPackage | null>(null)

  const isDirectMainPackageId = Boolean(id && uuidRegex.test(id))
  const selectedRouteId = id ?? DEFAULT_PACKAGES_ROUTE_ID
  const selectedRouteGroup = isDirectMainPackageId ? undefined : getPackageListRouteGroupById(selectedRouteId)
  const staticPackagesData: PackageCard[] = selectedRouteGroup
    ? selectedRouteGroup.cards.map((card, index) => ({
        ...card,
        image: pickImageByRoute(selectedRouteId, index)
      }))
    : []
  const hasCmsCards = Array.isArray(cmsCards) && cmsCards.length > 0
  const packagesData = hasCmsCards ? cmsCards : staticPackagesData
  const loading = loadedRouteId !== selectedRouteId || isCmsLoading

  useEffect(() => {
    const timer = setTimeout(() => setLoadedRouteId(selectedRouteId), 700)
    return () => clearTimeout(timer)
  }, [selectedRouteId])

  useEffect(() => {
    let cancelled = false

    const loadCmsPackages = async () => {
      setIsCmsLoading(true)
      setCmsCards(null)
      setCmsMainPackage(null)

      try {
        // Always fetch all main packages (needed for sibling tabs)
        const mainPackagesResponse = await cmsApi.getMainPackages(siteCountry)
        const mainPackages = Array.isArray(mainPackagesResponse?.data)
          ? (mainPackagesResponse.data as CmsMainPackage[])
          : []

        if (isDirectMainPackageId && id) {
          // Find current package and its siblings (same destination)
          const currentPkg = mainPackages.find(p => p.id === id)
          const currentDest = currentPkg?.destination?.trim().toLowerCase()
          const siblings = currentDest
            ? mainPackages.filter(p => p.destination?.trim().toLowerCase() === currentDest)
            : currentPkg ? [currentPkg] : []

          const siblingTabs: PackageRouteTab[] = siblings.map(p => ({
            id: p.id,
            label: p.title?.trim() || p.destination?.trim() || 'Package',
            routeId: p.id
          }))

          if (!cancelled) setCmsTabs(siblingTabs)

          const subRes = await cmsApi.getSubPackages(id, siteCountry)
          const subPackages = Array.isArray(subRes?.data) ? (subRes.data as CmsSubPackage[]) : []
          const mappedCards = mapSubPackages(subPackages, selectedRouteId, id, currentPkg?.destination)

          if (!cancelled) {
            setCmsMainPackage(currentPkg || { id })
            setCmsCards(mappedCards)
            rememberPackageContext(id, id, mappedCards.map(c => c.id))
          }
          return
        }

        // Normal slug-based route resolution
        if (!cancelled) setCmsTabs(buildCmsTabs(mainPackages))

        const matchedMainPackage = resolveMainPackageForRoute(mainPackages, selectedRouteId)

        if (!matchedMainPackage?.id) {
          if (!cancelled) { setCmsCards([]); setCmsMainPackage(null) }
          return
        }

        const subPackagesResponse = await cmsApi.getSubPackages(matchedMainPackage.id, siteCountry)
        const subPackages = Array.isArray(subPackagesResponse?.data)
          ? (subPackagesResponse.data as CmsSubPackage[])
          : []

        const mappedCards = mapSubPackages(subPackages, selectedRouteId, matchedMainPackage.id, matchedMainPackage.destination)

        if (!cancelled) {
          setCmsMainPackage(matchedMainPackage)
          setCmsCards(mappedCards)
          rememberPackageContext(matchedMainPackage.id, selectedRouteId, mappedCards.map(card => card.id))
        }
      } catch {
        if (!cancelled) { setCmsCards([]); setCmsTabs([]); setCmsMainPackage(null) }
      } finally {
        if (!cancelled) setIsCmsLoading(false)
      }
    }

    loadCmsPackages()
    return () => { cancelled = true }
  }, [selectedRouteId])

  // if (!selectedRouteGroup && !hasCmsCards && !loading) {
  //   return (
  //     <div className='max-w-4xl mx-auto px-6 py-40 text-center'>
  //       <h1 className='font-heading text-4xl font-bold text-brand-dark mb-4'>
  //         Package Category Not Found
  //       </h1>
  //       <p className='text-gray-600 mb-8'>
  //         Choose one of the available package categories below.
  //       </p>
  //       <div className='flex flex-wrap justify-center gap-3'>
  //         {allPackageRouteTabs.map(tab => (
  //           <Link
  //             key={tab.routeId}
  //             to={`/packages/${tab.routeId}`}
  //             className='px-5 py-2 rounded-full border border-gray-200 text-sm font-semibold text-brand-dark hover:border-brand-pink hover:text-brand-pink'
  //           >
  //             {tab.label}
  //           </Link>
  //         ))}
  //       </div>
  //     </div>
  //   )
  // }

  const displayDestinationName = hasCmsCards
    ? cmsMainPackage?.destination?.trim() || selectedRouteGroup?.destinationName || 'Destination'
    : selectedRouteGroup?.destinationName || 'Destination'

  const displayTitle = hasCmsCards
    ? cmsMainPackage?.title?.trim() || selectedRouteGroup?.title || 'Curated Packages'
    : selectedRouteGroup?.title || 'Curated Packages'

  const displaySubtitle = hasCmsCards
    ? cmsMainPackage?.description?.trim() || selectedRouteGroup?.subtitle || 'Curated travel packages'
    : selectedRouteGroup?.subtitle || 'Curated travel packages'

  // For direct UUID: show all sibling tabs. For slug: show matching tabs only.
  const sourceTabs = isDirectMainPackageId
    ? cmsTabs
    : cmsTabs.length > 0
      ? cmsTabs
      : selectedRouteGroup?.tabs ?? allPackageRouteTabs
  const visibleTabs = isDirectMainPackageId
    ? sourceTabs
    : sourceTabs.filter(tab => tab.routeId === selectedRouteId).length > 0
      ? sourceTabs.filter(tab => tab.routeId === selectedRouteId)
      : sourceTabs.slice(0, 1)

  const priceSuffix = hasCmsCards ? '/2 pax' : '/person'

  return (
    <>
      {/* HEADER */}
      <div className='bg-brand-dark pt-28 pb-12 h-[400px] relative overflow-hidden'>
        <div className='absolute inset-0 opacity-20'>
          <img
            className='w-full h-full object-cover'
            src='https://storage.googleapis.com/uxpilot-auth.appspot.com/84deee4332-9810b778ee3c9a1e3811.png'
            alt='Packages header'
          />
        </div>

        <div className='max-w-8xl mx-auto px-14 relative z-10 h-full flex flex-col justify-end pb-4 font-heading'>
          <h1 className='text-4xl md:text-6xl text-white font-bold mb-3'>
            Discover Packages
          </h1>
          <p className='text-gray-300 max-w-3xl'>
            {displayDestinationName} - {displaySubtitle}
          </p>

          <div className='mt-6 flex flex-wrap gap-2'>
            {visibleTabs.map(tab => {
              const isActive = selectedRouteId === tab.routeId
              return (
                <Link
                  key={tab.routeId}
                  to={`/packages/${tab.routeId}`}
                  className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wide border transition-colors ${
                    isActive
                      ? 'bg-brand-pink text-white border-brand-pink'
                      : 'bg-white/10 text-white border-white/20 hover:border-brand-pink'
                  }`}
                >
                  {tab.label}
                </Link>
              )
            })}
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div className='max-w-8xl mx-auto px-12 py-10'>
        <p className='mb-6 text-gray-600'>
          Showing <b>{packagesData.length}</b> packages for{' '}
          <b>{displayDestinationName}</b> ({displayTitle})
        </p>

        {loading ? (
          <div className='grid md:grid-cols-2 xl:grid-cols-3 gap-6'>
            {[...Array(6)].map((_, i) => (
              <div key={i} className='h-72 bg-gray-200 rounded-2xl animate-pulse' />
            ))}
          </div>
        ) : (
          <div className='grid md:grid-cols-2 xl:grid-cols-3 gap-6'>
            {packagesData.map(pkg => (
              <div
                key={pkg.id}
                className='bg-white rounded-2xl overflow-hidden shadow hover-lift group'
              >
                <div className='relative h-64 overflow-hidden'>
                  <img
                    src={pkg.image}
                    className='w-full h-full object-cover object-center group-hover:scale-110 transition'
                    alt={pkg.title}
                  />
                  {pkg.tag && (
                    <span className='absolute top-4 left-4 bg-brand-pink text-white text-xs px-3 py-1 rounded'>
                      {pkg.tag}
                    </span>
                  )}
                 
                </div>

                <div className='p-5'>
                  <div className='flex justify-between text-sm mb-2'>
                    <span className='flex items-center gap-1 text-gray-500'>
                      <FaMapMarkerAlt className='text-brand-pink' />
                      {pkg.location}
                    </span>
                    <span className='flex items-center gap-1 text-yellow-500'>
                      <FaStar /> {pkg.rating} ({pkg.reviews})
                    </span>
                  </div>

                  <h3 className='font-bold text-lg mb-2'>{pkg.title}</h3>

                  <div className='flex items-center gap-2 text-sm text-gray-500 mb-4'>
                    <FaClock /> {pkg.duration}
                  </div>

                  <ul className='text-xs text-gray-500 space-y-1.5 mb-4'>
                    {(pkg.keyHighlights ?? []).slice(0, 3).map((item, index) => (
                      <li key={index} className='flex items-start'>
                        <FaCheck className='text-green-500 mt-0.5 mr-2 shrink-0' />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <div className='flex justify-between items-center border-t pt-4'>
                    <div>
                      <p className='text-xs text-gray-400'>Starting from</p>
                      <p className='font-bold text-lg'>
                        {pkg.price}{' '}
                        <span className='text-sm'>{priceSuffix}</span>
                      </p>
                    </div>
                    <Link
                      to={`/package-detail/${pkg.id}${
                        pkg.mainPackageId
                          ? `?mainPackageId=${encodeURIComponent(pkg.mainPackageId)}&routeId=${encodeURIComponent(pkg.collectionRouteId || selectedRouteId)}`
                          : ''
                      }`}
                      className='px-4 py-2 bg-brand-dark text-white rounded hover:bg-brand-pink'
                    >
                      View
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default PackagesPage
